<template>
  <div id="step-metrics">
    <div class="metric" v-if="step.maneuver">
      <span :style="{ color: maneuverColor }">{{ step.maneuver.replace('-', ' ').toUpperCase() }}</span>
      <label>Maneuver</label>
    </div>

    <div class="metric">
      <span :style="{ color: durationColor }">{{ step.duration.text }}</span>
      <label>Duration</label>
    </div>

    <div class="metric">
      <span :style="{ color: distanceColor }">{{ step.distance.text }}</span>
      <label>Distance</label>
    </div>

    <div class="metric">
      <span :style="{ color: step.weather.avgtempF <= 32 ? scale('red') : 'inherit' }">{{ step.weather.avgtempF }}&#8457;</span>
      <label>Avg Temp</label>
    </div>

    <div class="metric">
      <span :style="{ color: scaleColor(step.weather.chanceoffog) }">{{ step.weather.chanceoffog }}%</span>
      <label>Fog Chance</label>
    </div>

    <div class="metric">
      <span :style="{ color: scaleColor(step.weather.chanceoffrost) }">{{ step.weather.chanceoffrost }}%</span>
      <label>Frost Chance</label>
    </div>

    <div class="metric">
      <span :style="{ color: scaleColor(step.weather.chanceofrain) }">{{ step.weather.chanceofrain }}%</span>
      <label>Rain Chance</label>
    </div>

    <div class="metric">
      <span :style="{ color: scaleColor(step.weather.chanceofsnow) }">{{ step.weather.chanceofsnow }}%</span>
      <label>Snow Chance</label>
    </div>

    <div class="metric">
      <span :style="{ color: visibilityColor }">{{ step.weather.visibility }}/10</span>
      <label>Visibility</label>
    </div>

    <div class="metric">
      <span :style="{ color: precipColor }">{{ step.weather.precipInches }}in.</span>
      <label>Precipitation</label>
    </div>

    <div class="metric">
      <span :style="{ color: windColor }">{{ step.weather.windspeedMiles }}mph</span>
      <label>Wind</label>
    </div>

    <div class="metric">
      <span :style="{ color: speedColor }">{{ formatSpeedLimit(step.road.Location.LinkInfo.SpeedCategory) }}</span>
      <label>Speed Limit</label>
    </div>

    <div class="metric">
      <span :style="{ color: roadClosures.length >= 2 ? chroma('red') : 'inherit' }">{{ roadClosures.length }}</span>
      <label>Road Closures</label>
    </div>

    <div class="metric">
      <span :style="{ color: minorConstructionColor }">{{ getConstructionIncidents(minorIncidents).length }}</span>
      <label>Minor Construction</label>
    </div>

    <div class="metric">
      <span :style="{ color: getConstructionIncidents(majorIncidents).length ? chroma('red') : 'inherit' }">{{ getConstructionIncidents(majorIncidents).length }}</span>
      <label>Major Construct</label>
    </div>

    <div class="metric">
      <span :style="{ color: getConstructionIncidents(criticalIncidents).length ? chroma('red') : 'inherit' }">{{ getConstructionIncidents(criticalIncidents).length }}</span>
      <label>Critical Construction</label>
    </div>

    <div class="metric">
      <span :style="{ color: crashColor }">{{ step.crashes.length }}</span>
      <label>YTD Crashes</label>
    </div>

  </div>
</template>

<script>
import chroma from 'chroma-js';

export default {
  data() {
    return { chroma }
  },
  computed: {
    step() {
      return this.$store.state.route.legs[0].steps[this.$store.state.stepIndex];
    },
    maneuverColor() {
      switch(this.step.maneuver) {
        case 'ferry':
        case 'ferry-train':
        case 'roundabout-left':
        case 'roundabout-right':
        case 'uturn-right':
        case 'uturn-left':
        case 'turn-left':
        case 'turn-sharp-left':
        case 'turn-sharp-right':
          return chroma('red');
        case 'merge':
        case 'fork-left':
        case 'fork-right':
          return chroma('yellow').darken();
        case 'turn-slight-right':
        case 'turn-right':
        case 'ramp-left':
        case 'ramp-right':
        case 'straight':
        default:
          return 'inherit';
      }
    },
    durationColor() {
      if(this.step.duration.value < 200) {
        return 'inherit';
      }

      if(this.step.duration.value < 400) {
        return chroma('yellow').darken();
      }

      return chroma('red');
    },
    distanceColor() {
      if(this.step.distance.value < 6000) {
        return 'inherit';
      }

      if(this.step.distance.value < 30000) {
        return chroma('yellow').darken();
      }

      return chroma('red');
    },
    visibilityColor() {
      if(this.step.weather.visibility <= 7) {
        return chroma('red');
      }

      if(this.step.weather.visibility <= 9) {
        return chroma('yellow').darken();
      }

      return 'inherit';
    },
    precipColor() {
      if(this.step.weather.precipInches > 1) {
        return chroma('red');
      }

      if(this.step.weather.precipInches > 0) {
        return chroma('yellow').darken();
      }

      return 'inherit';
    },
    windColor() {
      if(this.step.weather.windspeedMiles > 15) {
        return chroma('red');
      }

      if(this.step.weather.windspeedMiles >= 9) {
        return chroma('yellow').darken();
      }

      return 'inherit';
    },
    speedColor() {
      switch(this.step.road.Location.LinkInfo.SpeedCategory) {
        case 'SC1':
        case 'SC2':
          return chroma('red');
        case 'SC3':
        case 'SC4':
          return chroma('yellow').darken();
        case 'SC5':
        case 'SC6':
        case 'SC7':
        case 'SC8':
        default:
          return 'inherit';
      }
    },
    criticalIncidents() {
      return this.step.incidents.filter(i => i['CRITICALITY']['DESCRIPTION'] === 'critical');
    },
    minorIncidents() {
      return this.step.incidents.filter(i => i['CRITICALITY']['DESCRIPTION'] === 'minor');
    },
    majorIncidents() {
      return this.step.incidents.filter(i => i['CRITICALITY']['DESCRIPTION'] === 'major');
    },
    roadClosures() {
      return this.step.incidents.filter(i => i['TRAFFIC_ITEM_TYPE_DESC'] === 'ROAD_CLOSURE');
    },
    minorConstructionColor() {
      const mc = this.getConstructionIncidents(this.minorIncidents);

      if(mc.length > 4) {
        return chroma('red');
      }

      if(mc.length > 1) {
        return chroma('yellow').darken();
      }

      return 'inherit';
    },
    crashColor() {
      if(this.step.crashes.length > 100) {
        return chroma('red');
      }

      if(this.step.crashes.length > 0) {
        return chroma('yellow').darken();
      }

      return 'inherit';
    }
  },
  methods: {
    formatSpeedLimit(speed) {
      switch(speed) {
        case 'SC1':
          return '>80 MPH';
        case 'SC2':
          return '65-80 MPH';
        case 'SC3':
          return '55-64 MPH';
        case 'SC4':
          return '41-54 MPH';
        case 'SC5':
          return '31-40 MPH';
        case 'SC6':
          return '21-30 MPH';
        case 'SC7':
          return '6-20 MPH';
        case 'SC8':
          return '<6 MPH';
        default:
          return speed;
      }
    },
    scaleColor(percent) {
      return chroma.scale(['#333333', 'yellow', 'red']).domain([0, 100])(percent);
    },
    getConstructionIncidents(incidents) {
      return incidents.filter(i => i['TRAFFIC_ITEM_TYPE_DESC'] === 'CONSTRUCTION');
    }
  },
  watch: {
    step(s) {
      console.log(s.incidents.forEach(i => console.log(i['TRAFFIC_ITEM_TYPE_DESC'])));
    }
  }
}
</script>

<style>
#step-metrics {
  background: #fff;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  width: 600px;
  flex-wrap: wrap;
}

#step-metrics .metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  margin: 10px;
  width: 90px;
  white-space: nowrap;
}

#step-metrics .metric label {
  font-weight: bold;
  font-size: 12px;
}

@media(max-width: 600px) {
  #step-metrics {
    width: 100%;
    margin: 15px;
  }

  #trip-step {
    top: 0;
    left: 0;
  }

}
</style>
